import React, { useState, useEffect, useCallback } from 'react';
import './PerformanceReviewedSessionManagement.css';
import { supabase } from '../supabaseClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSync,
    faCheckCircle,
    faTimesCircle,
    faClipboardList,
    faSearch,
    faFilter,
} from '@fortawesome/free-solid-svg-icons';

// ── Constants ────────────────────────────────────────────────────────────────
const API_BASE = "https://fairpattern-secure-server-413055756357.europe-west1.run.app";

// ── Helper: call POST /admin-tool/v1/trigger/session-reviewed ────────────────
async function callSessionReviewedApi(sessionId) {
    const res = await fetch(`${API_BASE}/admin-tool/v1/trigger/session-reviewed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
    });
    if (!res.ok) {
        const detail = await res.text();
        throw new Error(`API error ${res.status}: ${detail}`);
    }
    return res.json();
}

// ── Toast component ──────────────────────────────────────────────────────────
function ToastContainer({ toasts }) {
    return (
        <div className="prsm-toast-container">
            {toasts.map((t) => (
                <div key={t.id} className={`prsm-toast ${t.type}`}>
                    {t.message}
                </div>
            ))}
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
const PerformanceReviewedSessionManagement = () => {
    const [sessions, setSessions] = useState([]);
    const [filteredSessions, setFilteredSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Per-row saving state: { [session_id]: 'saving' | 'success' | 'error' | null }
    const [savingStatus, setSavingStatus] = useState({});

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [reviewFilter, setReviewFilter] = useState('all'); // 'all' | 'reviewed' | 'pending'

    // Toast notifications
    const [toasts, setToasts] = useState([]);

    // ── Toast helper ──────────────────────────────────────────────────────────
    const showToast = useCallback((message, type = 'info') => {
        const id = Date.now();
        setToasts((prev) => [...prev, { id, message, type }]);
        setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
    }, []);

    // ── Fetch sessions from Supabase ─────────────────────────────────────────
    const fetchSessions = useCallback(async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('sessions')
                .select('session_id, session_name, domain_name, session_type, created_at, is_Reviewed')
                .order('created_at', { ascending: false });

            if (error) throw error;

            setSessions(data || []);
        } catch (err) {
            console.error('Error fetching sessions:', err.message);
            showToast(`Failed to load sessions: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    // ── Apply client-side filters ─────────────────────────────────────────────
    useEffect(() => {
        let result = [...sessions];

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(
                (s) =>
                    (s.session_name || '').toLowerCase().includes(q) ||
                    (s.domain_name || '').toLowerCase().includes(q)
            );
        }

        if (reviewFilter === 'reviewed') {
            result = result.filter((s) => s.is_Reviewed === true);
        } else if (reviewFilter === 'pending') {
            result = result.filter((s) => !s.is_Reviewed);
        }

        setFilteredSessions(result);
    }, [sessions, searchQuery, reviewFilter]);

    // ── Handle review status change ───────────────────────────────────────────
    const handleReviewChange = async (sessionId, newValue) => {
        const markAsReviewed = newValue === 'reviewed';

        // Optimistic UI update
        setSessions((prev) =>
            prev.map((s) =>
                s.session_id === sessionId ? { ...s, is_Reviewed: markAsReviewed } : s
            )
        );

        setSavingStatus((prev) => ({ ...prev, [sessionId]: 'saving' }));

        try {
            // 1. Update is_Reviewed in Supabase
            const { error: supabaseError } = await supabase
                .from('sessions')
                .update({ is_Reviewed: markAsReviewed })
                .eq('session_id', sessionId);

            if (supabaseError) throw supabaseError;

            // 2. If marking as reviewed, call the performance trigger API
            if (markAsReviewed) {
                showToast('Session marked as reviewed. Triggering performance analysis…', 'info');
                try {
                    const apiResult = await callSessionReviewedApi(sessionId);
                    console.log('Trigger API result:', apiResult);
                    showToast(
                        `✅ Performance analysis complete! ${apiResult.total_images_processed ?? ''} images processed.`,
                        'success'
                    );
                } catch (apiErr) {
                    // API trigger failed – still keep the is_Reviewed update
                    console.error('Trigger API error:', apiErr.message);
                    showToast(`⚠️ Saved as reviewed, but trigger API failed: ${apiErr.message}`, 'error');
                }
            } else {
                showToast('Session marked as not reviewed.', 'info');
            }

            setSavingStatus((prev) => ({ ...prev, [sessionId]: 'success' }));
            setTimeout(() => {
                setSavingStatus((prev) => ({ ...prev, [sessionId]: null }));
            }, 3000);
        } catch (err) {
            console.error('Error updating review status:', err.message);
            showToast(`Failed to update session: ${err.message}`, 'error');

            // Revert optimistic update on Supabase failure
            setSessions((prev) =>
                prev.map((s) =>
                    s.session_id === sessionId ? { ...s, is_Reviewed: !markAsReviewed } : s
                )
            );

            setSavingStatus((prev) => ({ ...prev, [sessionId]: 'error' }));
            setTimeout(() => {
                setSavingStatus((prev) => ({ ...prev, [sessionId]: null }));
            }, 4000);
        }
    };

    // ── Derived stats ─────────────────────────────────────────────────────────
    const totalCount = sessions.length;
    const reviewedCount = sessions.filter((s) => s.is_Reviewed).length;
    const pendingCount = totalCount - reviewedCount;

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="prsm-container">
            {/* Header */}
            <div className="prsm-header">
                <div className="prsm-header-left">
                    <h1>
                        <FontAwesomeIcon style={{ marginRight: 10, color: '#6366f1' }} />
                        Performance Reviewed Session Management
                    </h1>
                    <p>
                        Mark sessions as reviewed to trigger performance analysis. Reviewed sessions
                        automatically run the evaluation pipeline via the trigger API.
                    </p>
                </div>
                <div className="prsm-header-right">
                    <button
                        className="prsm-btn prsm-btn-secondary"
                        onClick={fetchSessions}
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon={faSync} spin={loading} />
                        {loading ? 'Loading…' : 'Refresh'}
                    </button>
                </div>
            </div>

            {/* Stats bar */}
            <div className="prsm-stats-bar">
                <div className="prsm-stat-card total">
                    <div className="prsm-stat-label">Total Sessions</div>
                    <div className="prsm-stat-value">{totalCount}</div>
                </div>
                <div className="prsm-stat-card reviewed">
                    <div className="prsm-stat-label">Reviewed</div>
                    <div className="prsm-stat-value">{reviewedCount}</div>
                </div>
                <div className="prsm-stat-card pending">
                    <div className="prsm-stat-label">Pending Review</div>
                    <div className="prsm-stat-value">{pendingCount}</div>
                </div>
            </div>

            {/* Filter bar */}
            <div className="prsm-filter-bar">
                <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
                    <FontAwesomeIcon
                        icon={faSearch}
                        style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }}
                    />
                    <input
                        className="prsm-search-input"
                        style={{ paddingLeft: 36 }}
                        type="text"
                        placeholder="Search by session or domain name…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FontAwesomeIcon icon={faFilter} style={{ color: '#94a3b8' }} />
                    <select
                        className="prsm-filter-select"
                        value={reviewFilter}
                        onChange={(e) => setReviewFilter(e.target.value)}
                    >
                        <option value="all">All Sessions</option>
                        <option value="reviewed">Reviewed Only</option>
                        <option value="pending">Pending Only</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="prsm-table-wrapper">
                {loading ? (
                    <div className="prsm-loading-state">
                        <div className="prsm-spinner" />
                        <span>Fetching sessions…</span>
                    </div>
                ) : (
                    <table className="prsm-table">
                        <thead>
                            <tr>
                                <th>Session Name</th>
                                <th>Domain</th>
                                <th>Type</th>
                                <th>Created At</th>
                                <th>Review Status</th>
                                <th>Mark Review</th>
                                <th>Save Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSessions.length > 0 ? (
                                filteredSessions.map((session) => {
                                    const isReviewed = session.is_Reviewed === true;
                                    const saving = savingStatus[session.session_id];

                                    return (
                                        <tr key={session.session_id}>
                                            {/* Session Name */}
                                            <td>
                                                <strong>{session.session_name || '—'}</strong>
                                                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 2 }}>
                                                    {session.session_id}
                                                </div>
                                            </td>

                                            {/* Domain */}
                                            <td>{session.domain_name || '—'}</td>

                                            {/* Type */}
                                            <td>
                                                <span className="prsm-type-badge">
                                                    {session.session_type || 'default'}
                                                </span>
                                            </td>

                                            {/* Created At */}
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                {session.created_at
                                                    ? new Date(session.created_at).toLocaleDateString(undefined, {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                    })
                                                    : '—'}
                                            </td>

                                            {/* Review Status badge (read-only display) */}
                                            <td>
                                                <span className={`prsm-review-badge ${isReviewed ? 'reviewed' : 'pending'}`}>
                                                    <FontAwesomeIcon
                                                        icon={isReviewed ? faCheckCircle : faTimesCircle}
                                                    />
                                                    {isReviewed ? 'Reviewed' : 'Not Reviewed'}
                                                </span>
                                            </td>

                                            {/* Review dropdown */}
                                            <td>
                                                <select
                                                    className={`prsm-review-select ${isReviewed ? 'is-reviewed' : 'not-reviewed'}`}
                                                    value={isReviewed ? 'reviewed' : 'not-reviewed'}
                                                    onChange={(e) =>
                                                        handleReviewChange(session.session_id, e.target.value)
                                                    }
                                                    disabled={saving === 'saving'}
                                                >
                                                    <option value="not-reviewed">⏳ Not Reviewed</option>
                                                    <option value="reviewed">✅ Reviewed</option>
                                                </select>
                                            </td>

                                            {/* Save status */}
                                            <td className="prsm-status-cell">
                                                {saving === 'saving' && (
                                                    <span className="prsm-status-saving">
                                                        <div className="prsm-spinner" />
                                                        Saving…
                                                    </span>
                                                )}
                                                {saving === 'success' && (
                                                    <span className="prsm-status-success">
                                                        <FontAwesomeIcon icon={faCheckCircle} />
                                                        Saved
                                                    </span>
                                                )}
                                                {saving === 'error' && (
                                                    <span className="prsm-status-error">
                                                        <FontAwesomeIcon icon={faTimesCircle} />
                                                        Error
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="7" className="prsm-empty-state">
                                        {searchQuery || reviewFilter !== 'all'
                                            ? 'No sessions match your current filters.'
                                            : 'No sessions found.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Toast notifications */}
            <ToastContainer toasts={toasts} />
        </div>
    );
};

export default PerformanceReviewedSessionManagement;
